import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Copy, CopyPlus, LayoutTemplate, Loader2, FolderOpen, TrendingUp, Star, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CHANNELS = ["", "instagram", "facebook", "tiktok", "blog"];
const EMPTY = { title: "", category: "", channel: "", text: "" };

export default function Templates({ onUseTemplate, role, currentUser }) {
  const sendToGenerator = (t) => {
    api.post(`/templates/${t.id}/use`).catch(() => {});
    onUseTemplate?.(t);
    toast.success(`Template "${t.title}" dikirim ke Generate Konten`);
  };
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");

  const load = async () => {
    setLoading(true);
    try { setTemplates((await api.get("/templates")).data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (t) => { setForm({ title: t.title, category: t.category || "", channel: t.channel || "", text: t.text }); setEditId(t.id); setOpen(true); };

  const save = async () => {
    if (!form.title.trim() || !form.text.trim()) { toast.error("Judul & isi wajib diisi"); return; }
    setSaving(true);
    const payload = { ...form, channel: form.channel === "umum" ? "" : form.channel };
    try {
      if (editId) { await api.put(`/templates/${editId}`, payload); toast.success("Template diperbarui"); }
      else { await api.post("/templates", payload); toast.success("Template ditambahkan"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus template ini?")) return;
    await api.delete(`/templates/${id}`);
    toast.success("Template dihapus"); load();
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Disalin"); };

  const duplicate = async (t) => {
    try {
      await api.post("/templates", { title: `${t.title} (Salinan)`, channel: t.channel || "", category: t.category || "", text: t.text });
      toast.success(`Template "${t.title}" diduplikasi`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const rate = async (id, rating) => {
    try {
      await api.post(`/templates/${id}/rate`, { rating });
      toast.success(`Dinilai ${rating} bintang`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const categories = Array.from(new Set(templates.map((t) => t.category || "Tanpa Kategori")));
  const maxUsage = Math.max(1, ...templates.map((t) => t.usage_count || 0));
  const mostUsed = templates.filter((t) => (t.usage_count || 0) > 0).sort((a, b) => b.usage_count - a.usage_count)[0];
  let visible = filter === "all" ? templates : templates.filter((t) => (t.category || "Tanpa Kategori") === filter);
  if (sort === "usage") visible = [...visible].sort((a, b) => (b.usage_count || 0) - (a.usage_count || 0));
  if (sort === "rating") visible = [...visible].sort((a, b) => (b.rating_avg || 0) - (a.rating_avg || 0));
  const grouped = {};
  visible.forEach((t) => { const k = t.category || "Tanpa Kategori"; (grouped[k] = grouped[k] || []).push(t); });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="bg-[#FFFFFF] border-[#E2E8F0] w-full sm:w-[200px]" data-testid="template-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
              <SelectItem value="all">Semua Kategori</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="bg-[#FFFFFF] border-[#E2E8F0] w-full sm:w-[160px]" data-testid="template-sort"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
              <SelectItem value="newest">Terbaru</SelectItem>
              <SelectItem value="usage">Paling Sering Dipakai</SelectItem>
              <SelectItem value="rating">Skor Tertinggi</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-500">{templates.length} template</span>
        </div>
        <Button onClick={openNew} data-testid="btn-add-template" className="bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl"><Plus size={16} className="mr-1" /> Tambah Template</Button>
      </div>

      {mostUsed && (
        <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl px-5 py-4 mb-6 flex items-center gap-3" data-testid="most-used-template">
          <div className="w-9 h-9 rounded-xl bg-[#F59E0B]/15 flex items-center justify-center"><TrendingUp size={18} className="text-[#B45309]" /></div>
          <div>
            <p className="text-sm font-semibold">Paling sering dipakai: {mostUsed.title}</p>
            <p className="text-xs text-slate-500">Sudah dipakai {mostUsed.usage_count} kali{mostUsed.category ? ` · kategori ${mostUsed.category}` : ""}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Memuat...</div>
      ) : templates.length === 0 ? (
        <div className="bg-[#FFFFFF] border border-dashed border-[#E2E8F0] rounded-2xl p-6 sm:p-12 text-center">
          <LayoutTemplate size={32} className="mx-auto text-slate-600 mb-3" />
          <p className="text-slate-600 mb-4">Belum ada template. Simpan caption favorit untuk digunakan kembali.</p>
          <Button onClick={openNew} className="bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl"><Plus size={16} className="mr-1" /> Tambah Template</Button>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.keys(grouped).map((cat) => (
            <div key={cat}>
              <div className="flex items-center gap-2 mb-3">
                <FolderOpen size={16} className="text-[#B45309]" />
                <h3 className="font-heading font-bold text-sm">{cat}</h3>
                <span className="text-xs text-slate-500">({grouped[cat].length})</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {grouped[cat].map((t) => (
                  <div key={t.id} data-testid={`template-card-${t.id}`} className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-5 hover:border-[#3B82F6] transition-colors flex flex-col">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-heading font-semibold text-sm">{t.title}</h4>
                        {t.channel && <span className="text-[11px] font-mono-code px-2 py-0.5 rounded bg-[#F1F5F9] text-slate-600 capitalize">{t.channel}</span>}
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => copy(t.text)} title="Salin teks" className="p-1.5 rounded-lg hover:bg-[#F1F5F9] text-slate-600 hover:text-[#059669] transition-colors"><Copy size={14} /></button>
                        <button onClick={() => duplicate(t)} title="Duplikasi template" data-testid={`duplicate-template-${t.id}`} className="p-1.5 rounded-lg hover:bg-[#F1F5F9] text-slate-600 hover:text-[#B45309] transition-colors"><CopyPlus size={14} /></button>
                        {(role === "super_admin" || t.created_by === currentUser?.id) && <button onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`} className="p-1.5 rounded-lg hover:bg-[#F1F5F9] text-slate-600 hover:text-[#2563EB] transition-colors"><Pencil size={14} /></button>}
                        {(role === "super_admin" || t.created_by === currentUser?.id) && <button onClick={() => remove(t.id)} data-testid={`delete-template-${t.id}`} className="p-1.5 rounded-lg hover:bg-[#F1F5F9] text-slate-600 hover:text-[#E11D48] transition-colors"><Trash2 size={14} /></button>}
                      </div>
                    </div>
                    <p className="text-xs text-slate-600 whitespace-pre-wrap line-clamp-5 flex-1">{t.text}</p>
                    <div className="mt-3" data-testid={`template-usage-${t.id}`}>
                      <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                        <span>Dipakai</span><span className="font-semibold text-slate-700">{t.usage_count || 0}x</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden">
                        <div className="h-full rounded-full bg-[#10B981] transition-all" style={{ width: `${Math.max(2, ((t.usage_count || 0) / maxUsage) * 100)}%` }} />
                      </div>
                    </div>
                    <div className="mt-2.5 flex items-center justify-between" data-testid={`template-rating-${t.id}`}>
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            key={n}
                            onClick={() => rate(t.id, n)}
                            data-testid={`rate-${t.id}-${n}`}
                            className="p-0.5 transition-transform hover:scale-125"
                            title={`Nilai ${n} bintang`}
                          >
                            <Star size={15} className={n <= Math.round(t.rating_avg || 0) ? "text-[#B45309] fill-[#F59E0B]" : "text-slate-600"} />
                          </button>
                        ))}
                      </div>
                      <span className="text-[11px] text-slate-500">
                        {t.rating_avg ? `${t.rating_avg} (${t.rating_count} penilaian)` : "Belum dinilai"}
                      </span>
                    </div>
                    <Button onClick={() => sendToGenerator(t)} variant="outline" size="sm" data-testid={`use-template-${t.id}`} className="mt-3 w-full border-[#2563EB]/40 bg-[#2563EB]/10 hover:bg-[#2563EB]/25 text-[#2563EB] rounded-xl text-xs">
                      <Sparkles size={13} className="mr-1" /> Gunakan di Generate Konten
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Template" : "Tambah Template"}</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">Simpan caption favorit untuk digunakan kembali di Generate & Jadwal.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-600">Judul</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="template-title-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="Promo Diskon" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Kategori</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="template-category-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="Promosi" />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Channel</Label>
                <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                  <SelectTrigger className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" data-testid="template-channel-select"><SelectValue placeholder="Umum" /></SelectTrigger>
                  <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
                    {CHANNELS.map((c) => <SelectItem key={c || "umum"} value={c || "umum"} className="capitalize">{c || "Umum"}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Isi Template</Label>
              <Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={6} data-testid="template-text-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="Tulis caption template..." />
            </div>
            <Button onClick={save} disabled={saving} data-testid="template-save-button" className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
              {saving ? <Loader2 size={16} className="animate-spin mr-1" /> : null}{editId ? "Simpan Perubahan" : "Tambah Template"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
